import { Injectable } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { AbstractRender } from 'src/app/abstract/render.abstract';
import { WidgetsAbstract } from 'src/app/abstract/widgets/widgets.abstract';
import { Logger } from 'src/app/core/logger';
import { FieldOptionModel } from 'src/app/models/widgets/field-option.model';
import { WidgetsFieldModel } from 'src/app/models/widgets/widgets-field.model';
import { WidgetsInputModel } from 'src/app/models/widgets/widgets-input.model';
import { numericInputValidator } from 'src/app/validators/numeric-input.validator';
import { CloneService } from '../clone/clone.service';
import { Config } from 'src/app/config';

@Injectable({
  providedIn: 'root'
})
export class RenderFieldService extends AbstractRender {
  constructor(private logger: Logger) {
    super();
  }

  /**
   * Renders a field or radio layout node to a formly config object
   *
   * @param model The field or radio model to render
   * @param config The Formly Config object to render to
   * @param widgetRoot The root of the widget tree
   * @param formlyRoot The root of the formly object
   * @returns
   */
  render(
    widget: WidgetsAbstract,
    config: FormlyFieldConfig[],
    widgetRoot: WidgetsAbstract,
    formlyRoot: FormlyFieldConfig,
    isInsideQuestion: boolean,
    isRecursive: boolean
  ) {
    const model = widget as WidgetsFieldModel;

    let fieldGroupPrimitive: FormlyFieldConfig = {};
    let field: FormlyFieldConfig = {
      key: model.key,
    };
    let fieldLabel: FormlyFieldConfig = {};

    if (model.template) {
      const modelTemplate = JSON.parse(model.template);
      fieldGroupPrimitive = Object.assign({}, fieldGroupPrimitive, modelTemplate);
      // TODO: Check if this is really necessary
      field = Object.assign({}, field, modelTemplate);
    }
    // Mix created input with widget template
    if (model.template) {
      const modelTemplate = JSON.parse(model.template);
      field = { ...modelTemplate, ...field };
      // Deep merge not working... Merge props manually
      const mergedprops = {
        ...field.props,
        ...modelTemplate.props,
      };
      field.props = mergedprops;
    }

    if (model.fieldTemplate) {
      const fieldTemplate = JSON.parse(model.fieldTemplate);
      field = Object.assign({}, field, fieldTemplate);
    }


    // Override labels on Fields
    // Go up to custom model and overrides labels
    const labelOverrides: Map<string, string> = new Map<string, string>();
    CloneService.getLabelOverrides(model, widgetRoot, labelOverrides);
    const overridedString = CloneService.overrideText(
      model.label,
      labelOverrides
    );

    // Add label
    if (model.label) {
      fieldLabel.template = '<div class="prop-label">' + overridedString + '</div>';
    }    

    // Includes needs String type
    // eslint-disable-next-line
    const typesWithOptions: Array<String> = ['radio', 'select'];
    if (typesWithOptions.includes(model.type)) {
      if (model.type == 'select') {
        field.type = 'uw-select';
      } else if (model.type == 'radio') {
        field.type = 'uw-radio';
      } else {
        field.type = model.type;
      }

      field.props = {};

      // If options are provided, use them. If not, use sample data
      // model instanceof WidgetsRadioModel
      if (model.options && model.options.length > 0) {
        field.props.options = model.options;

        // TODO: Set default value pending
        // Get the last model.option.selected and add field.defaultValue = to its value
        // Use native for; faster than some... https://stackoverflow.com/questions/13843972/angular-js-break-foreach
        for (let i = model.options.length - 1; i >= 0; i--) {
          const opt: FieldOptionModel = model.options[i];
          if (opt.selected) {
            field.defaultValue = opt.value;
            break;
          }
        }
      } else {
        field.props = {
          options: [
            { value: 1, label: 'Yes' },
            { value: 2, label: 'No' },
            { value: 3, label: 'N/A', disabled: true },
          ],
        };
      }
    } else if (model.type === 'multi-select') {
      field.type = 'uw-select';
      field.props = {
        multiple: true,
        selectAllOption: 'Select All',
        options: [
          { value: 1, label: 'Option 1' },
          { value: 2, label: 'Option 2' },
          { value: 3, label: 'Option 3' },
          { value: 4, label: 'Option 4', disabled: true },
        ],
      };
      if (overridedString) { field.props.label = model.label; }
    } else if (model.type === 'time' || model.type === 'date') {
      // Minimal browser time/date input support
      field.type = 'uw-input';
      field.props = {
        label: model.label && overridedString ? overridedString : model.type,
        type: model.type,
        required: true,
      };
    } else if (model.type === 'timeRange') {
      //From
      let field2: FormlyFieldConfig = {
        key: model.key,
      };
      field2.key = model.key + '_from';
      field2.type = 'uw-input';
      field2.props = {
        label: model.label && overridedString ? overridedString : 'From:',
        type: 'time',
        required: false
      };
      field2.props.required = model.required ? model.required : false;
      if (model.template) {
        const modelTemplate = JSON.parse(model.template);
        field2 = { ...modelTemplate, ...field2 };
        // Deep merge not working... Merge props manually
        const mergedprops = {
          ...field2.props,
          ...modelTemplate.props,
        };
        field2.props = mergedprops;
      }

      config.push(field2);
      //To
      field.key = model.key + '_to';
      field.type = 'uw-input';
      field.props = {
        label: model.label && overridedString ? overridedString : 'To:',
        type: 'time',
        required: true,
      };

      //field.validators = { validation: ['timeRangeValidation'] };
    } else if (model.type === 'input') {
      const inputModel: WidgetsInputModel = model as WidgetsInputModel;


      field.type = 'uw-input';
      field.props = {
        attributes: {
          onpaste: 'return ' + inputModel.isPasteEnabled + ';',
          inputmode: 'numeric'
        }
      };
      if (overridedString) {
        field.props.label = model.label;
      }
      if (inputModel.isNumeric) {
        field.props.type = 'number';
        // field.props.keypress = (myField, event) => numericInputValidator(myField, event);
        field.props.keyEnforce = 'numericInput';        

        if (inputModel.isDecimal) { field.props.isDecimal = inputModel.isDecimal; }
        if (inputModel.isPercentage) { 
          field.validators = {
            validation: ['percentage']
          }
          field.props.isPercentage = inputModel.isPercentage; 
        }

        // Validations for current year
        if (inputModel.isMinCurrentYear) {
          field.validators = {
            validation: ['minCurrentYear']
          };
        } else if (inputModel.minNum !== undefined) {
          field.props.min = inputModel.minNum;
        }

        if (inputModel.isMaxCurrentYear) {
          field.validators = {
            validation: ['maxCurrentYear']
          };
        } else if (inputModel.maxNum !== undefined) {
          field.props.max = inputModel.maxNum;
        }

        if (inputModel.maxTextLength) {
          // field.props.maxLength = inputModel.maxTextLength;
          const maxFunction =
            "{ if (this.value.length >" + inputModel.maxTextLength
            + ") { this.value = this.value.slice(0, " + inputModel.maxTextLength
            + "); } }";
          if (!field.props.attributes) { field.props.attributes = {}; }
          field.props.attributes.onInput = maxFunction;
        }
        if (inputModel.minTextLength) { field.props.minLength = inputModel.minTextLength; }
      } else {
        // This is for text
        if (inputModel.pattern) { field.props.pattern = inputModel.pattern; }

        if (inputModel.minTextLength) { field.props.minLength = inputModel.minTextLength; }
        if (inputModel.maxTextLength) { field.props.maxLength = inputModel.maxTextLength; }
      }
      field.props.required = true;

      // Suffix
      if (inputModel.suffix) {
        field.props.addonRight = {
          text: inputModel.suffix
        };
      }
      // TODO: Suffix wrapper
    } else if (model.type === 'textarea') {
      field.type = 'textarea';
      if (!field.props) {
        field.props = {};
      }
      if (model.rows) {
        field.props.rows = model.rows;
      } else {
        field.props.rows = Config.CONSTANTS.textAreaRows;
      }
      if (model.maxLength) {
        field.props.maxLength = model.maxLength;
      } else {
        field.props.maxLength = Config.CONSTANTS.textAreaMaxLength;
      }
      if (overridedString) { field.props.label = model.label; }
    } else if (model.type === 'checkbox') {
      field.type = 'uw-checkbox';
      field.props = {};
      if (overridedString) { field.props.label = model.label; }
    } else {
      // Other fields: input,...
      field.type = model.type;
      field.props = {};
      if (overridedString) { field.props.label = model.label; }
    }

    /* Override validations - from template
        TODO: override parameters
        Example for data validation:
              "data": {
                "condition": "$comparator",
                "left": "$leftField",
                "right": "$rightField" }
        */
    for (const validation of model.children) {
      try {
        const valTemplate: string = validation.getTemplate();
        if (valTemplate) {
          const template = JSON.parse(valTemplate);

          // FIXME: Check if validation is only for field or for form...
          if (template.name === 'date' || template.name === 'timeRange') {
            // else if it is for whole form...
            const validators = { validators: { validation: [template] } };

            // FIXME: For now apply at the root level for date validation
            formlyRoot.validators = { validation: [template] };
            // rootValidation.push( template );
          } else {
            // Else apply at the field level
            field.validators = {
              validation: [template],
            };
          }
        }
      } catch (err) {
        this.logger.warn('validation exeception:' + err);
      }
    }

    // Propagate field props
    if (model.description) {
      field.props.description = model.description;
    }
    if (model.required) {
      field.props.required = model.required;
    }
    if (model.commentKey) {
      field.props.commentKey = model.commentKey;
    }

    // 4. Prepare groups
    fieldGroupPrimitive.fieldGroupClassName = 'primitive-field';
    fieldGroupPrimitive.fieldGroup = [];    

    // 5. Render
    fieldLabel.className = 'prop-label';
    fieldGroupPrimitive.fieldGroup?.push(fieldLabel);    
    field.className = 'prop-field';
    fieldGroupPrimitive.fieldGroup?.push(field);
    fieldGroupPrimitive.wrappers = ['field'];
    config.push(fieldGroupPrimitive);    
  }
}
